import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// TypeScript interfaces for comment data
interface User {
  _id: string;
  firstName: string;
  lastName: string;
  username: string;
  profilePhoto: {
    url?: string;
  };
}

interface Comment {
  _id: string;
  post: string;
  user: User;
  content: string;
  status: string;
  isEdited: boolean;
  likes: string[];
  reports: string[];
  editHistory: any[];
  createdAt: string;
  updatedAt: string;
  likeCount: number;
  reportCount: number;
  isLiked: boolean;
}

interface CommentState {
  comments: Comment[];
  loading: boolean;
  error: string | null;
}

const initialState: CommentState = {
  comments: [],
  loading: false,
  error: null,
};

const CommentSlice = createSlice({
  name: 'comment',
  initialState,
  reducers: {
    setComments: (state, action: PayloadAction<Comment[]>) => {
      state.comments = action.payload;
      state.loading = false;
      state.error = null;
    },
    addComment: (state, action: PayloadAction<Comment>) => {
      state.comments.unshift(action.payload);
      state.error = null;
    },
    editComment: (state, action: PayloadAction<Comment>) => {
      const index = state.comments.findIndex(comment => comment._id === action.payload._id);
      if (index !== -1) {
        state.comments[index] = action.payload;
      }
      state.error = null;
    },
    deleteComment: (state, action: PayloadAction<string>) => {
      state.comments = state.comments.filter(comment => comment._id !== action.payload);
      state.error = null;
    },
    updateComment: (state, action: PayloadAction<Partial<Comment> & { _id: string }>) => {
      const index = state.comments.findIndex(comment => comment._id === action.payload._id);
      if (index !== -1) {
        state.comments[index] = { ...state.comments[index], ...action.payload };
      }
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearComments: (state) => {
      state.comments = [];
      state.loading = false;
      state.error = null;
    },
  },
});

export const { 
  setComments, 
  addComment, 
  editComment, 
  deleteComment, 
  updateComment, 
  setLoading, 
  setError, 
  clearComments 
} = CommentSlice.actions;

export type { Comment, User, CommentState };
export default CommentSlice.reducer;
